import React, { useState } from 'react';
import { BanknoteIcon, CalendarIcon, LockIcon, UserIcon, DollarSignIcon, CheckCircleIcon, XCircleIcon, CreditCardIcon } from 'lucide-react';
export function VirtualTerminal() {
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [processingStatus, setProcessingStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const [reference, setReference] = useState('');
  const formatCardNumber = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Add space every 4 digits
    const formatted = digits.replace(/(\d{4})(?=\d)/g, '$1 ');
    return formatted.substring(0, 19); // Limit to 16 digits + 3 spaces
  };
  const formatExpiryDate = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Format as MM/YY
    if (digits.length > 2) {
      return `${digits.substring(0, 2)}/${digits.substring(2, 4)}`;
    }
    return digits;
  };
  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCardNumber(formatCardNumber(e.target.value));
  };
  const handleExpiryDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExpiryDate(formatExpiryDate(e.target.value));
  };
  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Limit to 3-4 digits
    const digits = e.target.value.replace(/\D/g, '');
    setCvv(digits.substring(0, 4));
  };
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numbers and decimals
    const value = e.target.value.replace(/[^\d.]/g, '');
    // Ensure only one decimal point
    const parts = value.split('.');
    if (parts.length > 2) {
      return;
    }
    setAmount(value);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Basic validation
    if (!cardNumber || !expiryDate || !cvv || !cardholderName || !amount) {
      alert('Please fill in all required fields');
      return;
    }
    // Simulate payment processing
    setProcessingStatus('processing');
    // Generate a random reference number
    const newReference = `REF-${Math.floor(Math.random() * 1000000).toString().padStart(6, '0')}`;
    setReference(newReference);
    // Simulate API call with timeout
    setTimeout(() => {
      // 80% chance of success for demo purposes
      const isSuccess = Math.random() < 0.8;
      setProcessingStatus(isSuccess ? 'success' : 'error');
    }, 2000);
  };
  const resetForm = () => {
    setCardNumber('');
    setExpiryDate('');
    setCvv('');
    setCardholderName('');
    setAmount('');
    setDescription('');
    setProcessingStatus('idle');
    setReference('');
  };
  return <div className="max-w-3xl mx-auto" data-id="element-4317">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-4318">
        <div className="bg-[#1a2042] px-6 py-4 text-white" data-id="element-4319">
          <h1 className="text-xl font-semibold flex items-center" data-id="element-4320">
            <BanknoteIcon size={24} className="mr-2" data-id="element-4321" />
            Virtual MOTO Terminal
          </h1>
          <p className="text-sm text-gray-300 mt-1" data-id="element-4322">
            Securely process Mail Order / Telephone Order transactions
          </p>
        </div>
        {processingStatus === 'idle' && <form onSubmit={handleSubmit} className="p-6 space-y-6" data-id="element-4323">
            <div className="space-y-4" data-id="element-4324">
              <h2 className="text-lg font-medium text-gray-800 border-b pb-2" data-id="element-4325">
                Payment Details
              </h2>
              <div data-id="element-4326">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4327">
                  Amount*
                </label>
                <div className="relative" data-id="element-4328">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4329">
                    <DollarSignIcon size={16} className="text-gray-400" data-id="element-4330" />
                  </div>
                  <input type="text" value={amount} onChange={handleAmountChange} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="0.00" required data-id="element-4331" />
                </div>
              </div>
              <div data-id="element-4332">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4333">
                  Description (Optional)
                </label>
                <input type="text" value={description} onChange={e => setDescription(e.target.value)} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Product purchase" data-id="element-4334" />
              </div>
            </div>
            <div className="space-y-4" data-id="element-4335">
              <h2 className="text-lg font-medium text-gray-800 border-b pb-2" data-id="element-4336">
                Card Information
              </h2>
              <div data-id="element-4337">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4338">
                  Card Number*
                </label>
                <div className="relative" data-id="element-4339">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4340">
                    <CreditCardIcon size={16} className="text-gray-400" data-id="element-4341" />
                  </div>
                  <input type="text" value={cardNumber} onChange={handleCardNumberChange} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="XXXX XXXX XXXX XXXX" required data-id="element-4342" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-4343">
                <div data-id="element-4344">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4345">
                    Expiry Date*
                  </label>
                  <div className="relative" data-id="element-4346">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4347">
                      <CalendarIcon size={16} className="text-gray-400" data-id="element-4348" />
                    </div>
                    <input type="text" value={expiryDate} onChange={handleExpiryDateChange} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="MM/YY" required data-id="element-4349" />
                  </div>
                </div>
                <div data-id="element-4350">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4351">
                    CVV/CVC*
                  </label>
                  <div className="relative" data-id="element-4352">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4353">
                      <LockIcon size={16} className="text-gray-400" data-id="element-4354" />
                    </div>
                    <input type="text" value={cvv} onChange={handleCvvChange} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="XXX" required data-id="element-4355" />
                  </div>
                </div>
              </div>
              <div data-id="element-4356">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4357">
                  Cardholder Name*
                </label>
                <div className="relative" data-id="element-4358">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4359">
                    <UserIcon size={16} className="text-gray-400" data-id="element-4360" />
                  </div>
                  <input type="text" value={cardholderName} onChange={e => setCardholderName(e.target.value)} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Name as it appears on card" required data-id="element-4361" />
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-md text-sm text-gray-600" data-id="element-4362">
              <p className="flex items-center" data-id="element-4363">
                <LockIcon size={14} className="mr-2 text-gray-500" data-id="element-4364" />
                Card information is securely processed and encrypted
              </p>
            </div>
            <div className="flex justify-end" data-id="element-4365">
              <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition-colors" data-id="element-4366">
                Process Payment
              </button>
            </div>
          </form>}
        {processingStatus === 'processing' && <div className="p-6 flex flex-col items-center justify-center h-80" data-id="element-4367">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600 mb-4" data-id="element-4368"></div>
            <h2 className="text-lg font-medium text-gray-800 mb-2" data-id="element-4369">
              Processing Payment
            </h2>
            <p className="text-gray-600" data-id="element-4370">
              Please wait while we process your transaction...
            </p>
          </div>}
        {processingStatus === 'success' && <div className="p-6 flex flex-col items-center justify-center h-80" data-id="element-4371">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-4372">
              <CheckCircleIcon size={40} className="text-green-600" data-id="element-4373" />
            </div>
            <h2 className="text-lg font-medium text-gray-800 mb-2" data-id="element-4374">
              Payment Successful
            </h2>
            <p className="text-gray-600 mb-2" data-id="element-4375">
              The transaction has been approved.
            </p>
            <div className="bg-gray-100 rounded-md p-4 w-full max-w-md mt-4" data-id="element-4376">
              <p className="text-sm text-gray-700 mb-1" data-id="element-4377">
                <span className="font-medium" data-id="element-4378">Amount:</span> $
                {parseFloat(amount).toFixed(2)}
              </p>
              <p className="text-sm text-gray-700 mb-1" data-id="element-4379">
                <span className="font-medium" data-id="element-4380">Reference:</span> {reference}
              </p>
              <p className="text-sm text-gray-700 mb-1" data-id="element-4381">
                <span className="font-medium" data-id="element-4382">Card:</span> **** **** ****{' '}
                {cardNumber.slice(-4)}
              </p>
              <p className="text-sm text-gray-700" data-id="element-4383">
                <span className="font-medium" data-id="element-4384">Date:</span>{' '}
                {new Date().toLocaleString()}
              </p>
            </div>
            <button onClick={resetForm} className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition-colors" data-id="element-4385">
              New Transaction
            </button>
          </div>}
        {processingStatus === 'error' && <div className="p-6 flex flex-col items-center justify-center h-80" data-id="element-4386">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4" data-id="element-4387">
              <XCircleIcon size={40} className="text-red-600" data-id="element-4388" />
            </div>
            <h2 className="text-lg font-medium text-gray-800 mb-2" data-id="element-4389">
              Transaction Failed
            </h2>
            <p className="text-gray-600 mb-2" data-id="element-4390">
              The payment could not be processed.
            </p>
            <div className="bg-red-50 border border-red-100 rounded-md p-4 w-full max-w-md mt-4" data-id="element-4391">
              <p className="text-sm text-red-800" data-id="element-4392">
                Error: Transaction declined by issuing bank. Please try a
                different payment method or contact the card issuer.
              </p>
            </div>
            <button onClick={resetForm} className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition-colors" data-id="element-4393">
              Try Again
            </button>
          </div>}
      </div>
    </div>;
}